import { toast } from 'react-toastify';

import { createAsyncThunk } from '@reduxjs/toolkit';

import localization from '../../localization';
import auth from '../../services/auth';
import {
  LOGIN_SUCCESS,
  LOGOUT,
  SET_REALM,
  SET_SELECTED_CUSTOMER,
  SILENT_LOGIN,
  UPDATE_INCLUDES_DEFAULT,
  UPDATE_NEXWAY_STATE,
} from '../constants/actionTypes';
import { nexwayStateSelector } from '../selectors/Account';

const setRealm = (realm) => (dispatch) => {
  localStorage.setItem('realm', realm);

  dispatch({
    type: SET_REALM,
    payload: { realm },
  });
};

function broadcastToParents(message) {
  const targets = [];

  if (window.parent !== window) {
    targets.push({ target: window.parent, name: 'parent' });
  }

  if (window.top !== window) {
    targets.push({ target: window.top, name: 'top' });
  }

  if (window.opener) {
    targets.push({ target: window.opener, name: 'opener' });
  }

  targets.forEach(({ target, name }) => {
    try {
      target.postMessage(message, '*');
    } catch (error) {
      console.warn(`Failed to send to ${name}:`, error);
    }
  });
}

const loginComplete =
  ({ result, realm, onSuccess = () => {}, onError = () => {} }) =>
  async (dispatch) => {
    try {
      const { access_token } = result;
      const user = { access_token, ...auth.decodeToken(access_token) };

      dispatch(setRealm(realm));

      dispatch({
        type: LOGIN_SUCCESS,
        payload: {
          user,
        },
      });

      onSuccess();

      broadcastToParents({
        type: 'auth_tokens',
        ...result,
      });

      toast(
        `${localization.t('general.welcomeBack')}${user.given_name ? `, ${user.given_name}` : ''}!`,
      );
    } catch (e) {
      onError(e);
    }
  };

const loginTwoFA =
  ({ credentials = {}, onSuccess = () => {}, onError = () => {} }) =>
  async (dispatch) => {
    try {
      const { realm, token, code } = credentials;
      const result = await auth.loginWith2FA(token, code, realm);
      dispatch(loginComplete({ result, realm, onSuccess, onError }));
    } catch (e) {
      onError(e);
    }
  };

const loginWithBackupCode =
  ({ credentials = {}, onSuccess = () => {}, onError = () => {} }) =>
  async (dispatch) => {
    try {
      const { realm, email, backupCode } = credentials;
      const result = await auth.signInWithBackupCode(email, backupCode, realm);
      dispatch(loginComplete({ result, realm, onSuccess, onError }));
    } catch (e) {
      onError(e);
    }
  };

const loginUser =
  ({ credentials = {}, onSuccess = () => {}, onError = () => {}, onTwoFA = () => {} }) =>
  async (dispatch) => {
    try {
      const { realm, username, password } = credentials;
      const result = await auth.loginWithEmailAndPassword(username, password, realm);

      const { access_token, twoFA } = result;

      if (!twoFA) {
        dispatch(loginComplete({ result, realm, onSuccess, onError }));
      }

      onTwoFA(access_token);
    } catch (e) {
      onError(e);
    }
  };

const loginWithGoogle =
  ({ credentials = {}, onSuccess = () => {} }) =>
  async (dispatch) => {
    try {
      const { realm, idToken } = credentials;
      const result = await auth.loginWithGoogle(idToken, realm);

      dispatch(loginComplete({ result, realm, onSuccess }));
    } catch (e) {
      console.error('Google login failed');
    }
  };

const setUserData = (token) => (dispatch) => {
  const user = { access_token: token, ...auth.decodeToken(token) };

  dispatch({
    type: SILENT_LOGIN,
    payload: {
      user,
    },
  });
};

const setNexwayState = (newState) => (dispatch) => {
  localStorage.setItem('nexwayState', JSON.stringify(newState));
  dispatch({
    type: UPDATE_NEXWAY_STATE,
    payload: {
      nexwayState: { ...newState },
    },
  });
};

const setSelectedCustomer = createAsyncThunk(
  SET_SELECTED_CUSTOMER,
  (newSelectedCustomer, { getState, dispatch }) => {
    const nexwayState = nexwayStateSelector(getState());
    const { selectedCustomer, recentCustomers = [] } = nexwayState;
    const newRecentCustomers = recentCustomers
      .filter(({ id }) => id !== newSelectedCustomer?.id)
      .slice(-3);

    selectedCustomer && newRecentCustomers.push({ ...selectedCustomer, group_type: 'Recent' });

    dispatch(
      setNexwayState({
        ...nexwayState,
        selectedCustomer: newSelectedCustomer,
        recentCustomers: newRecentCustomers,
      }),
    );
  },
);

const setIncludesDefault = (includesDefault) => (dispatch) => {
  localStorage.setItem('includesDefault', JSON.stringify(includesDefault));

  dispatch({
    type: UPDATE_INCLUDES_DEFAULT,
    payload: {
      includesDefault,
    },
  });
};

const logout = () => async (dispatch) => {
  auth.logout();

  dispatch({
    type: LOGOUT,
  });

  toast(localization.t('general.sessionEnded'));
};

export {
  loginUser,
  loginWithGoogle,
  loginTwoFA,
  logout,
  setUserData,
  setNexwayState,
  setIncludesDefault,
  setRealm,
  setSelectedCustomer,
  loginWithBackupCode,
};
